import React from 'react';
import { XMarkIcon, HeartIcon, TagIcon, ClockIcon } from '@heroicons/react/24/outline';
import { ChickInfo } from '../../api/chicks';

interface ChickDetailsViewProps {
  chick: ChickInfo;
  onClose: () => void;
}

export default function ChickDetailsView({ chick, onClose }: ChickDetailsViewProps) {
  const getAgeColor = (ageInDays: number) => {
    if (ageInDays < 7) return 'text-green-600';
    if (ageInDays < 21) return 'text-yellow-600';
    if (ageInDays < 42) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAgeLabel = (ageInDays: number) => {
    if (ageInDays === 0) return 'Today';
    if (ageInDays === 1) return '1 day old';
    if (ageInDays < 7) return `${ageInDays} days old`;
    if (ageInDays < 14) return `${Math.floor(ageInDays / 7)} week old`;
    if (ageInDays < 28) return `${Math.floor(ageInDays / 7)} weeks old`;
    return `${Math.floor(ageInDays / 30)} month${Math.floor(ageInDays / 30) > 1 ? 's' : ''} old`;
  };

  const getRearMethodBadgeColor = (method: string) => {
    switch (method) {
      case 'PARENT_REARED':
        return 'bg-emerald-100 text-emerald-800';
      case 'HAND_REARED':
        return 'bg-cyan-100 text-cyan-800';
      case 'FOSTER_REARED':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <HeartIcon className="h-8 w-8 text-pink-400 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Chick Details</h3>
            <p className="text-sm text-slate-500">{chick.species?.commonName || 'Unknown Species'}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-slate-50/80 rounded-lg p-4">
          <h4 className="text-md font-medium text-slate-900 mb-3">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Species</label>
              <p className="mt-1 text-sm text-slate-900">
                {chick.species?.commonName || 'Unknown'}
              </p>
              {chick.species?.scientificName && (
                <div className="text-sm text-slate-500 italic">
                  {chick.species.scientificName}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Cage Number</label>
              <p className="mt-1 text-sm text-slate-900">{chick.cageNumber}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Age</label>
              <p className={`mt-1 text-sm font-medium ${getAgeColor(chick.ageInDays)}`}>
                {getAgeLabel(chick.ageInDays)} ({chick.ageInDays} days)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Hatch Date</label>
              <p className="mt-1 text-sm text-slate-900">
                {chick.hatchDate.toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Rearing Information */}
        <div className="bg-slate-50/80 rounded-lg p-4">
          <h4 className="text-md font-medium text-slate-900 mb-3">Rearing Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Rear Method</label>
              <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium mt-1 ${getRearMethodBadgeColor(chick.rearMethod)}`}>
                {chick.rearMethod.replace('_', ' ')}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium mt-1 ${
                chick.isBanded 
                  ? 'bg-emerald-100 text-emerald-800' 
                  : 'bg-amber-100 text-amber-800'
              }`}>
                {chick.isBanded ? (
                  <>
                    <TagIcon className="h-3 w-3 mr-1" />
                    Banded
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-3 w-3 mr-1" />
                    Unbanded
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Parent Information */}
        {(chick.maleParent || chick.femaleParent) && (
          <div className="bg-slate-50/80 rounded-lg p-4">
            <h4 className="text-md font-medium text-slate-900 mb-3">Parent Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {chick.maleParent && (
                <div>
                  <label className="block text-sm font-medium text-slate-700">Male Parent</label>
                  <p className="mt-1 text-sm text-slate-900">
                    {chick.maleParent.bandId || 'Unknown'}
                  </p>
                </div>
              )}
              {chick.femaleParent && (
                <div>
                  <label className="block text-sm font-medium text-slate-700">Female Parent</label>
                  <p className="mt-1 text-sm text-slate-900">
                    {chick.femaleParent.bandId || 'Unknown'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Banding Information */}
        {chick.isBanded && (
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h4 className="text-md font-medium text-green-900 mb-3 flex items-center">
              <TagIcon className="h-5 w-5 mr-2" />
              Banding Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {chick.bandNumber && (
                <div>
                  <label className="block text-sm font-medium text-green-700">Band Number</label>
                  <p className="mt-1 text-sm font-mono text-green-900 font-semibold">
                    {chick.bandNumber}
                  </p>
                </div>
              )}
              {chick.bandingDate && (
                <div>
                  <label className="block text-sm font-medium text-green-700">Banding Date</label>
                  <p className="mt-1 text-sm text-green-900">
                    {chick.bandingDate.toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {chick.notes && (
          <div className="bg-slate-50/80 rounded-lg p-4">
            <h4 className="text-md font-medium text-slate-900 mb-3">Notes</h4>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{chick.notes}</p>
          </div>
        )}

        {/* Timestamps */}
        <div className="bg-slate-50/80 rounded-lg p-4">
          <h4 className="text-md font-medium text-slate-900 mb-3">Record Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600">
            <div>
              <label className="block text-sm font-medium text-slate-700">Created</label>
              <p className="mt-1">{new Date(chick.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Last Updated</label>
              <p className="mt-1">{new Date(chick.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-6">
        <button
          onClick={onClose}
          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80"
        >
          Close
        </button>
      </div>
    </div>
  );
} 